<template>
  <SectionCard01CarouselLogo02
    v-if="contextData"
    :title="contextData.title"
    :description="contextData.description"
    :items="contextItems"
    :theme="theme"
  />
</template>

<script setup lang="ts">
  import type { ISuiteProps } from '@/components/Shared/Suite/type.ts';

  interface IItem {
    name: string;
    description: string;
    link: string;
    linkName: string;
    thumbnail: string;
    wistiaCode?: string;
    trackingCode?: string;
    logos: string[];
    tag?: string;
  }

  interface IHardwaresSplitData {
    title: string;
    description?: string;
    items: IItem[];
  }

  interface IHardwaresSplit {
    component: 'hardwares-split';
    meta: {
      navigable?: string;
    };
    data: IHardwaresSplitData;
  }

  type IProps = ISuiteProps<IHardwaresSplit>;

  const props = defineProps<IProps>();

  const solutionsStore = useSolutionsStore();
  const { themeStyle } = storeToRefs(solutionsStore);

  const { provideFormatUrl } = useFormatUrl();
  const { provideSuiteThumbnailUrl, provideSuiteLogoUrl } = useImagePath(
    props.urlConnector
  );

  const contextData = computed((): IHardwaresSplitData => props.suiteData.data);

  const contextItems = computed((): IItem[] => {
    return contextData.value.items.map(formatCarouselItem);
  });

  const theme = computed((): TTheme => {
    switch (themeStyle.value) {
      case 'sustainable':
        return 'green';
      case 'connected':
        return 'cyan';
      case 'ai':
        return 'purple';
      case 'operations':
        return 'yellow';
      default:
        return 'blue';
    }
  });

  const formatCarouselItem = (item: IItem): IItem => ({
    ...item,
    thumbnail: provideSuiteThumbnailUrl(item.thumbnail),
    link: provideFormatUrl(item.link),
    logos: item.logos.map(provideSuiteLogoUrl),
  });

  export type { IHardwaresSplit, IHardwaresSplitData, IItem };
</script>
